import React, { useEffect } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'

import './sidebar.scss'

// Sidebars only slide in from the LEFT. The right edge belongs to the manager assistant
// column (app/component/core/manager-assistant/), which is permanent rather than a sidebar;
// the auto-planner — the last right-side sidebar — now renders inside it.
const UNDERLAY_STYLE = Object.freeze({
  NORMAL: 'normal',
  LIGHT: 'light',
  DISABLED: 'disabled'
})

export function Sidebar ({
  onClose,
  children,
  width,
  showNavBar,
  disableCloseByKeyboardShortcut,
  underlayStyle,
  underlayClosesSidebar,
  contentClassName
}) {
  const [canStartTransition, setCanStartTransition] = React.useState(false)

  const handleKeypress = (evt) => {
    // Esc keypress
    if (evt.keyCode === 27 && onClose && !disableCloseByKeyboardShortcut) {
      onClose()
    }
  }

  // when we scroll mouse wheel while while we have the underlay active, that scroll
  // event is propagated onto calendar sections, so user can scroll calendar while
  // it's covered by the underlay
  const propagateUnderlayScrollToCalendar = () => {
    const overlay = document.querySelector('.ds-sidebar-underlay')
    const scrollableDivs = document.querySelectorAll('.ds-c-section')

    const handleScroll = (e) => {
      let currentScrollableDiv = null
      scrollableDivs.forEach(div => {
        const rect = div.getBoundingClientRect()
        if (e.clientX >= rect.left && e.clientX <= rect.right &&
            e.clientY >= rect.top && e.clientY <= rect.bottom) {
          currentScrollableDiv = div
        }
      })

      if (currentScrollableDiv) {
        currentScrollableDiv.scrollTop += e.deltaY
        currentScrollableDiv.scrollLeft += e.deltaX
      }
    }

    if (overlay && scrollableDivs) {
      overlay.removeEventListener('wheel', handleScroll)
      overlay.addEventListener('wheel', handleScroll)
    }
  }
  setTimeout(() => { propagateUnderlayScrollToCalendar() }, 100)

  useEffect(() => {
    // Nothing to do about Intercom any more: its launcher sits in the bottom-RIGHT corner,
    // which a left-hand sidebar never covers. The manager assistant column owns that offset
    // now (it calls miscUtil.intercomAdjustPosition itself).
    setCanStartTransition(true)
    document.addEventListener('keydown', handleKeypress, false)
    return () => {
      document.removeEventListener('keydown', handleKeypress, false)
    }
  }, [])

  const offset = canStartTransition ? '0' : `-${width}`

  return (
    <div
      className={classNames('ds-sidebar', {
        'no-underlay': underlayStyle === UNDERLAY_STYLE.DISABLED,
        'is-open': canStartTransition,
        'show-nav-bar': showNavBar
      })}
    >
      {underlayStyle !== UNDERLAY_STYLE.DISABLED && (
        <div onClick={underlayClosesSidebar ? onClose : () => {}} className={'ds-sidebar-underlay' + (underlayStyle === UNDERLAY_STYLE.LIGHT ? ' light-underlay' : '')} />
      )}
      <div
        className={classNames('ds-sidebar-content', contentClassName)}
        style={{
          width,
          marginLeft: offset,
          marginRight: 'auto'
        }}
      >
        {children}
      </div>
    </div>
  )
}

Sidebar.UNDERLAY_STYLE = UNDERLAY_STYLE

Sidebar.propTypes = {
  onClose: PropTypes.func,
  children: PropTypes.element.isRequired,
  width: PropTypes.string,
  underlayStyle: PropTypes.string,
  underlayClosesSidebar: PropTypes.bool,
  showNavBar: PropTypes.bool,
  disableCloseByKeyboardShortcut: PropTypes.bool,
  contentClassName: PropTypes.string
}
Sidebar.defaultProps = {
  onClose: undefined,
  width: '20rem',
  underlayStyle: UNDERLAY_STYLE.NORMAL,
  underlayClosesSidebar: true,
  showNavBar: false,
  disableCloseByKeyboardShortcut: false,
  contentClassName: undefined
}
